import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/api';
import { useOrgStore } from '@/store/orgStore';
import { toast } from 'sonner';
import { DndContext, closestCorners, DragEndEvent, useDraggable, useDroppable } from '@dnd-kit/core';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertTriangle, ArrowUpRight, CalendarClock, Clock3, DollarSign, GripVertical, MoreHorizontal } from 'lucide-react';

const stageColors: Record<string, string> = {
  Prospecto: '#94A3B8',
  'Contacto realizado': '#2563EB',
  'Reunion / demo': '#0EA5E9',
  'Propuesta enviada': '#F59E0B',
  Negociacion: '#8B5CF6',
  'Cerrado ganado': '#22C55E',
  'Cerrado perdido': '#EF4444',
};

const healthColors: Record<string, string> = {
  saludable: 'bg-[#DCFCE7] text-[#15803D] border-[#22C55E]/20',
  riesgo: 'bg-[#FFFBEB] text-[#B45309] border-[#F59E0B]/20',
  alto_riesgo: 'bg-[#FEE2E2] text-[#B91C1C] border-[#EF4444]/20',
};

const closedStages = ['Cerrado ganado', 'Cerrado perdido'];

function formatCLP(n: number) {
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(n || 0);
}

function OpportunityCard({ opp, stages, onMove }: { opp: any; stages: any[]; onMove: (id: string, stage: string) => void }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: opp.id, data: opp });
  const style: React.CSSProperties = {
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    opacity: isDragging ? 0.95 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`touch-manipulation border border-[#E2E8F0] bg-white p-4 shadow-sm transition-transform duration-150 ease-out hover:border-[#2563EB]/30 motion-reduce:transition-none ${isDragging ? 'scale-[1.02] rotate-[0.5deg] cursor-grabbing shadow-lg' : 'cursor-grab'}`}
      data-testid={`pipeline-card-${opp.id}`}
      {...listeners}
      {...attributes}
    >
      <div className="flex items-start gap-2">
        <GripVertical size={15} className="mt-0.5 shrink-0 text-[#CBD5E1]" />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <Link to={`/oportunidades/${opp.id}`} className="line-clamp-2 text-sm font-semibold text-[#0F172A] hover:text-[#2563EB]">
                {opp.name}
              </Link>
              <p className="mt-1 text-xs text-[#64748B]">{opp.account_name || 'Sin empresa'}</p>
            </div>
            <div className="flex items-center gap-2">
              <ArrowUpRight size={14} className="text-[#94A3B8]" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[#E2E8F0] bg-white text-[#64748B] hover:bg-[#F8FAFC]" aria-label="Mover">
                    <MoreHorizontal size={14} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {stages.map((s: any) => (
                    <DropdownMenuItem key={s.id} onClick={() => onMove(opp.id, s.name)}>
                      Mover a {s.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-[#475569]">
            <DollarSign size={13} className="shrink-0 text-[#22C55E]" />
            <span className="font-semibold tabular-nums text-[#0F172A]">{formatCLP(opp.amount || 0)}</span>
            <Badge variant="outline" className="border-[#E2E8F0] bg-[#F8FAFC] text-[#475569] tabular-nums">{opp.probability}%</Badge>
            <Badge
              variant="outline"
              className={`text-xs font-semibold ${healthColors[opp.health_band] || 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]'}`}
              title={[
                `Salud: ${typeof opp.health_score === 'number' ? opp.health_score : '-'}`,
                opp.health_recommendation ? `Recomendación: ${opp.health_recommendation}` : '',
              ].filter(Boolean).join(' • ')}
            >
              {typeof opp.health_score === 'number' ? opp.health_score : '-'}
            </Badge>
          </div>
          <div className="mt-3 grid gap-2">
            <div className="flex items-center gap-2 text-xs text-[#64748B]">
              <Clock3 size={12} className="shrink-0 text-[#94A3B8]" />
              <span className="tabular-nums">{opp.days_in_stage || 0} días en etapa</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#64748B]">
              <CalendarClock size={12} className={opp.next_step_date && opp.next_step_date < new Date().toISOString().slice(0, 10) ? 'text-[#DC2626]' : 'text-[#94A3B8]'} />
              <span>{opp.next_step || 'Sin próximo paso'} · {opp.next_step_date || 'Sin fecha'}</span>
            </div>
            <div className="text-xs text-[#64748B]">
              Última actividad: {opp.last_activity_at ? opp.last_activity_at.slice(0, 10) : 'Sin actividad'}
            </div>
            {(opp.health_band === 'alto_riesgo' || !opp.last_activity_at) && (
              <div className="flex items-center gap-2 rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-2 py-1 text-[11px] text-[#92400E]">
                <AlertTriangle size={12} />
                {opp.health_band === 'alto_riesgo' ? 'Alto riesgo' : 'Riesgo de inactividad'}
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function PipelinePage() {
  const [opportunities, setOpportunities] = useState<any[]>([]);
  const [stages, setStages] = useState<any[]>([]);
  const [lostDialogOpen, setLostDialogOpen] = useState(false);
  const [lostReason, setLostReason] = useState('');
  const [pendingMove, setPendingMove] = useState<{ opportunityId: string; targetStage: string } | null>(null);
  const { currentOrgId } = useOrgStore();

  const fetchData = useCallback(async () => {
    if (!currentOrgId) return;
    const [oppsRes, stagesRes] = await Promise.all([
      api.get('/opportunities'),
      api.get('/pipeline-stages'),
    ]);
    setOpportunities(oppsRes.data || []);
    setStages(stagesRes.data || []);
  }, [currentOrgId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totals = useMemo(() => ({
    total: opportunities.reduce((sum, item) => sum + Number(item.amount || 0), 0),
    weighted: opportunities.filter((item) => !closedStages.includes(item.stage)).reduce((sum, item) => sum + Math.round(Number(item.amount || 0) * (Number(item.probability || 0) / 100)), 0),
    won: opportunities.filter((item) => item.stage === 'Cerrado ganado').length,
    open: opportunities.filter((item) => !closedStages.includes(item.stage)).length,
  }), [opportunities]);

  const moveOpportunity = async (opportunityId: string, targetStage: string, reason?: string) => {
    const currentOpp = opportunities.find((item) => item.id === opportunityId);
    if (!currentOpp || currentOpp.stage === targetStage) return;
    try {
      await api.patch(`/opportunities/${opportunityId}/stage`, {
        stage: targetStage,
        lost_reason: reason,
      });
      toast.success(`Oportunidad movida a ${targetStage}`);
      await fetchData();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'No fue posible mover la oportunidad');
      await fetchData();
    }
  };

  const moveOpportunityFromMenu = async (opportunityId: string, targetStage: string) => {
    const currentOpp = opportunities.find((item) => item.id === opportunityId);
    if (!currentOpp || currentOpp.stage === targetStage) return;
    if (targetStage === 'Cerrado perdido') {
      setPendingMove({ opportunityId, targetStage });
      setLostReason(currentOpp.lost_reason || '');
      setLostDialogOpen(true);
      return;
    }
    await moveOpportunity(opportunityId, targetStage);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    const opportunityId = String(active.id);
    let targetStage = String(over.id);

    const targetOpp = opportunities.find((item) => item.id === targetStage);
    if (targetOpp) {
      targetStage = targetOpp.stage;
    } else {
      const stageDoc = stages.find((item) => item.id === targetStage);
      if (stageDoc) targetStage = stageDoc.name;
    }

    const currentOpp = opportunities.find((item) => item.id === opportunityId);
    if (!currentOpp || currentOpp.stage === targetStage) return;

    if (targetStage === 'Cerrado perdido') {
      setPendingMove({ opportunityId, targetStage });
      setLostReason(currentOpp.lost_reason || '');
      setLostDialogOpen(true);
      return;
    }

    await moveOpportunity(opportunityId, targetStage);
  };

  const confirmLostMove = async () => {
    if (!pendingMove) return;
    await moveOpportunity(pendingMove.opportunityId, pendingMove.targetStage, lostReason);
    setLostDialogOpen(false);
    setPendingMove(null);
    setLostReason('');
  };

  return (
    <div data-testid="pipeline-page">
      <div className="overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur mb-6">
        <div className="h-1.5 bg-gradient-to-r from-[#2563EB] via-[#8B5CF6] to-[#22C55E]" />
        <div className="flex flex-col gap-4 p-5 md:p-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Pipeline</h1>
            <p className="text-sm text-[#475569] mt-1">Arrastra oportunidades entre etapas finales del flujo comercial.</p>
          </div>
          <div className="grid w-full grid-cols-2 gap-3 sm:grid-cols-4 lg:w-auto">
            <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">Pipeline total</p>
              <p className="mt-1 text-sm font-semibold tabular-nums text-[#0F172A]">{formatCLP(totals.total)}</p>
            </div>
            <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">Ponderado</p>
              <p className="mt-1 text-sm font-semibold tabular-nums text-[#0F172A]">{formatCLP(totals.weighted)}</p>
            </div>
            <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">Abiertas</p>
              <p className="mt-1 text-sm font-semibold tabular-nums text-[#0F172A]">{totals.open}</p>
            </div>
            <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">Ganadas</p>
              <p className="mt-1 text-sm font-semibold tabular-nums text-[#0F172A]">{totals.won}</p>
            </div>
          </div>
        </div>
      </div>

      <DndContext collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
        <div className="space-y-3" data-testid="pipeline-board">
          <AccordionPrimitive.Root type="multiple" defaultValue={stages.map((s) => s.id)} className="rounded-2xl border border-[#E2E8F0] bg-white/90 shadow-sm overflow-hidden">
            {stages.map((stage) => {
              const stageOpportunities = opportunities.filter((item) => item.stage === stage.name);
              const stageTotal = stageOpportunities.reduce((sum, item) => sum + Number(item.amount || 0), 0);
              return (
                <AccordionPrimitive.Item key={stage.id} value={stage.id} className="border-b border-[#E2E8F0] last:border-b-0">
                  <AccordionPrimitive.Header className="flex">
                    <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between py-4 text-sm font-medium transition-all hover:no-underline text-left px-4 [&[data-state=open]>svg]:rotate-180">
                    <div className="flex w-full items-center gap-3 pr-2">
                      <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: stageColors[stage.name] || '#94A3B8' }} />
                      <div className="min-w-0 flex-1 text-left">
                        <div className="text-sm font-semibold text-[#0F172A]">{stage.name}</div>
                        <div className="text-xs tabular-nums text-[#64748B]">{stageOpportunities.length} negocios · {formatCLP(stageTotal)}</div>
                      </div>
                      {stageOpportunities.length > 0 && (
                        <Badge variant="outline" className="border-[#E2E8F0] bg-[#F8FAFC] text-[#475569]">
                          {stageOpportunities.length}
                        </Badge>
                      )}
                    </div>
                    <span className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-md border border-[#E2E8F0] bg-[#F8FAFC] text-[#64748B] transition-transform">
                      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                        <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    </AccordionPrimitive.Trigger>
                  </AccordionPrimitive.Header>
                  <AccordionPrimitive.Content className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                    <div className="px-4 pb-4 pt-0">
                    {stageOpportunities.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-[#E2E8F0] bg-[#F8FAFC] p-4 text-sm text-[#94A3B8]">
                        Sin oportunidades en esta etapa.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {stageOpportunities.map((opportunity) => (
                          <OpportunityCard key={opportunity.id} opp={opportunity} stages={stages} onMove={moveOpportunityFromMenu} />
                        ))}
                      </div>
                    )}
                    </div>
                  </AccordionPrimitive.Content>
                </AccordionPrimitive.Item>
              );
            })}
          </AccordionPrimitive.Root>
        </div>
      </DndContext>

      <Dialog open={lostDialogOpen} onOpenChange={setLostDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Motivo de cierre perdido</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Motivo</Label>
            <Textarea value={lostReason} onChange={(e) => setLostReason(e.target.value)} className="min-h-[110px] border-[#E2E8F0]" placeholder="Ej: competencia, precio, presupuesto, no responde..." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setLostDialogOpen(false); setPendingMove(null); }} className="border-[#E2E8F0]">Cancelar</Button>
            <Button onClick={confirmLostMove} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
